/**
 * src/services/api.js
 *
 * Service layer between React components and the Cloudflare Worker API.
 * User identity comes from the session cookie — never from query params or body.
 * Falls back to localStorage when the API is unavailable.
 */

const BASE = import.meta.env.VITE_API_URL || ''

async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE}/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  if (res.status === 401) {
    window.location.reload()
    return
  }
  if (!res.ok) throw new Error(`API error ${res.status} on ${path}`)
  return res.json()
}

function lsGet(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) ?? JSON.stringify(fallback)) }
  catch { return fallback }
}

function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* quota */ }
}

// ─── MOODS ───────────────────────────────────────────────────────────────────

export async function getMoods() {
  try { return await apiFetch('/moods') }
  catch (e) { console.warn('[api] getMoods fallback', e); return lsGet('softly-log', []) }
}

export async function saveMood(entry) {
  const next = [entry, ...lsGet('softly-log', [])]
  lsSet('softly-log', next)
  try { return await apiFetch('/moods', { method: 'POST', body: JSON.stringify({ ...entry }) }) }
  catch (e) { console.warn('[api] saveMood fallback', e); return { ok: true, local: true } }
}

// ─── TODO LOG ─────────────────────────────────────────────────────────────────

export async function getTodoLog() {
  try { return await apiFetch('/todos/log') }
  catch (e) { console.warn('[api] getTodoLog fallback', e); return lsGet('softly-todo-log', {}) }
}

export async function saveTodoLogDay(date, count) {
  const log = lsGet('softly-todo-log', {})
  log[date] = count
  lsSet('softly-todo-log', log)
  try { return await apiFetch('/todos/log', { method: 'POST', body: JSON.stringify({ date, count }) }) }
  catch (e) { console.warn('[api] saveTodoLogDay fallback', e); return { ok: true, local: true } }
}

// ─── TODOS ────────────────────────────────────────────────────────────────────

export async function getTodos() {
  try { return await apiFetch('/todos') }
  catch (e) { console.warn('[api] getTodos fallback', e); return lsGet('softly-todos', []) }
}

export async function saveTodos(todos) {
  lsSet('softly-todos', todos)
  try { return await apiFetch('/todos', { method: 'PUT', body: JSON.stringify({ todos }) }) }
  catch (e) { console.warn('[api] saveTodos fallback', e); return { ok: true, local: true } }
}

// ─── REFLECTIONS ──────────────────────────────────────────────────────────────

export async function getReflections() {
  try { return await apiFetch('/reflections') }
  catch (e) { console.warn('[api] getReflections fallback', e); return lsGet('softly-reflections', []) }
}

export async function saveReflection(entry) {
  const next = [entry, ...lsGet('softly-reflections', [])].slice(0, 12)
  lsSet('softly-reflections', next)
  try { return await apiFetch('/reflections', { method: 'POST', body: JSON.stringify({ ...entry }) }) }
  catch (e) { console.warn('[api] saveReflection fallback', e); return { ok: true, local: true } }
}

export async function updateReflection(id, { text, emoji }) {
  const all = lsGet('softly-reflections', [])
  lsSet('softly-reflections', all.map(r => r.id === id ? { ...r, text, emoji: emoji || null } : r))
  try { return await apiFetch(`/reflections/${id}`, { method: 'PUT', body: JSON.stringify({ text, emoji }) }) }
  catch (e) { console.warn('[api] updateReflection fallback', e); return { ok: true, local: true } }
}

// ─── GOOD THINGS ──────────────────────────────────────────────────────────────

export async function getGoodThings() {
  try { return await apiFetch('/good-things') }
  catch (e) { console.warn('[api] getGoodThings fallback', e); return lsGet('softly-good-things', []) }
}

export async function saveGoodThing(item) {
  const next = [item, ...lsGet('softly-good-things', [])]
  lsSet('softly-good-things', next)
  try { return await apiFetch('/good-things', { method: 'POST', body: JSON.stringify({ ...item }) }) }
  catch (e) { console.warn('[api] saveGoodThing fallback', e); return { ok: true, local: true } }
}

export async function deleteGoodThing(localId) {
  const next = lsGet('softly-good-things', []).filter(g => g.id !== localId)
  lsSet('softly-good-things', next)
  try { return await apiFetch(`/good-things/${localId}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteGoodThing fallback', e); return { ok: true, local: true } }
}

// ─── BOOKS ───────────────────────────────────────────────────────────────────

export async function getBooks() {
  try { return await apiFetch('/books') }
  catch (e) { console.warn('[api] getBooks fallback', e); return lsGet('softly-books', []) }
}

export async function saveBook(book) {
  const next = [book, ...lsGet('softly-books', [])]
  lsSet('softly-books', next)
  try { return await apiFetch('/books', { method: 'POST', body: JSON.stringify({ ...book }) }) }
  catch (e) { console.warn('[api] saveBook fallback', e); return { ok: true, local: true } }
}

export async function updateBook(localId, updates) {
  const books = lsGet('softly-books', [])
  const next  = books.map(b => b.id === localId ? { ...b, ...updates } : b)
  lsSet('softly-books', next)
  try { return await apiFetch(`/books/${localId}`, { method: 'PUT', body: JSON.stringify({ ...updates }) }) }
  catch (e) { console.warn('[api] updateBook fallback', e); return { ok: true, local: true } }
}

export async function deleteBook(localId) {
  const next = lsGet('softly-books', []).filter(b => b.id !== localId)
  lsSet('softly-books', next)
  try { return await apiFetch(`/books/${localId}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteBook fallback', e); return { ok: true, local: true } }
}

export async function importBooks(books) {
  const existing = lsGet('softly-books', [])
  const titles   = new Set(existing.map(b => b.title?.toLowerCase()))
  const toAdd    = books.filter(b => b.title && !titles.has(b.title.toLowerCase()))
  lsSet('softly-books', [...existing, ...toAdd])
  try { return await apiFetch('/books/import', { method: 'POST', body: JSON.stringify({ books }) }) }
  catch (e) { console.warn('[api] importBooks fallback', e); return { ok: true, added: toAdd.length, local: true } }
}

// ─── BINGO ────────────────────────────────────────────────────────────────────

export async function getBingo() {
  try { return await apiFetch('/bingo') }
  catch (e) { console.warn('[api] getBingo fallback', e); return lsGet('softly-bingo', { items: [], done: [], freeText: '' }) }
}

export async function saveBingo(data) {
  lsSet('softly-bingo', data)
  try { return await apiFetch('/bingo', { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveBingo fallback', e); return { ok: true, local: true } }
}

// ─── COLOUR WALK ──────────────────────────────────────────────────────────────

export async function getColourWalk() {
  try { return await apiFetch('/colour-walk') }
  catch (e) { console.warn('[api] getColourWalk fallback', e); return lsGet('softly-colour', { idx: 0, found: [] }) }
}

export async function saveColourWalk(data) {
  lsSet('softly-colour', data)
  try { return await apiFetch('/colour-walk', { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveColourWalk fallback', e); return { ok: true, local: true } }
}

// ─── HOBBIES ──────────────────────────────────────────────────────────────────

export async function getHobbies() {
  try { return await apiFetch('/hobbies') }
  catch (e) { console.warn('[api] getHobbies fallback', e); return lsGet('softly-hobbies-tried', []) }
}

export async function saveHobbies(hobbies) {
  lsSet('softly-hobbies-tried', hobbies)
  try { return await apiFetch('/hobbies', { method: 'PUT', body: JSON.stringify({ data: hobbies }) }) }
  catch (e) { console.warn('[api] saveHobbies fallback', e); return { ok: true, local: true } }
}

// ─── WALK STATE ───────────────────────────────────────────────────────────────

const WALK_LS_KEYS = { walk: 'softly-walk', 'walk-mission': 'act-walk-mission', walking: 'act-walking' }

export async function getWalkState(key) {
  try { return await apiFetch(`/walk/${key}`) }
  catch (e) { console.warn('[api] getWalkState fallback', e); return lsGet(WALK_LS_KEYS[key] || key, {}) }
}

export async function saveWalkState(key, data) {
  lsSet(WALK_LS_KEYS[key] || key, data)
  try { return await apiFetch(`/walk/${key}`, { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveWalkState fallback', e); return { ok: true, local: true } }
}

// ─── LEARNING GOALS ───────────────────────────────────────────────────────────

export async function getGoals() {
  try { return await apiFetch('/goals') }
  catch (e) { console.warn('[api] getGoals fallback', e); return lsGet('softly-learning-goals', []) }
}

export async function saveGoals(goals) {
  lsSet('softly-learning-goals', goals)
  try { return await apiFetch('/goals', { method: 'PUT', body: JSON.stringify({ goals }) }) }
  catch (e) { console.warn('[api] saveGoals fallback', e); return { ok: true, local: true } }
}

// ─── ACTIVITIES ───────────────────────────────────────────────────────────────

export async function getActivities() {
  try { return await apiFetch('/activities') }
  catch (e) { console.warn('[api] getActivities fallback', e); return lsGet('act-activities', {}) }
}

export async function saveActivities(log) {
  lsSet('act-activities', log)
  try { return await apiFetch('/activities', { method: 'PUT', body: JSON.stringify({ data: log }) }) }
  catch (e) { console.warn('[api] saveActivities fallback', e); return { ok: true, local: true } }
}

// ─── GARDEN ───────────────────────────────────────────────────────────────────

export async function getGardenPlants() {
  try { return await apiFetch('/garden/plants') }
  catch (e) { console.warn('[api] getGardenPlants fallback', e); return lsGet('softly-garden-plants', []) }
}

export async function saveGardenPlants(plants) {
  lsSet('softly-garden-plants', plants)
  try { return await apiFetch('/garden/plants', { method: 'PUT', body: JSON.stringify({ data: plants }) }) }
  catch (e) { console.warn('[api] saveGardenPlants fallback', e); return { ok: true, local: true } }
}

export async function getGardenCare() {
  try { return await apiFetch('/garden/care') }
  catch (e) { console.warn('[api] getGardenCare fallback', e); return lsGet('softly-garden-care', {}) }
}

export async function saveGardenCare(data) {
  lsSet('softly-garden-care', data)
  try { return await apiFetch('/garden/care', { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveGardenCare fallback', e); return { ok: true, local: true } }
}

export async function getGardenTips() {
  try { return await apiFetch('/garden/tips') }
  catch (e) { console.warn('[api] getGardenTips fallback', e); return lsGet('softly-garden-my-tips', {}) }
}

export async function saveGardenTips(data) {
  lsSet('softly-garden-my-tips', data)
  try { return await apiFetch('/garden/tips', { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveGardenTips fallback', e); return { ok: true, local: true } }
}

export async function getGardenJournal() {
  try { return await apiFetch('/garden/journal') }
  catch (e) { console.warn('[api] getGardenJournal fallback', e); return lsGet('softly-garden-journal', []) }
}

export async function saveGardenJournal(entries) {
  lsSet('softly-garden-journal', entries)
  try { return await apiFetch('/garden/journal', { method: 'PUT', body: JSON.stringify({ entries }) }) }
  catch (e) { console.warn('[api] saveGardenJournal fallback', e); return { ok: true, local: true } }
}

// ─── COOKING ──────────────────────────────────────────────────────────────────

export async function getCookingTips() {
  try { return await apiFetch('/cooking/tips') }
  catch (e) { console.warn('[api] getCookingTips fallback', e); return lsGet('softly-cooking-notes-v2', {}) }
}

export async function saveCookingTips(data) {
  lsSet('softly-cooking-notes-v2', data)
  try { return await apiFetch('/cooking/tips', { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveCookingTips fallback', e); return { ok: true, local: true } }
}

export async function getCookingRecipes() {
  try { return await apiFetch('/cooking/recipes') }
  catch (e) { console.warn('[api] getCookingRecipes fallback', e); return lsGet('softly-cooking-recipes', []) }
}

export async function saveCookingRecipes(recipes) {
  lsSet('softly-cooking-recipes', recipes)
  try { return await apiFetch('/cooking/recipes', { method: 'PUT', body: JSON.stringify({ recipes }) }) }
  catch (e) { console.warn('[api] saveCookingRecipes fallback', e); return { ok: true, local: true } }
}

export async function getMealPlan(type) {
  try { return await apiFetch(`/cooking/meal-plan/${type}`) }
  catch (e) {
    console.warn('[api] getMealPlan fallback', e)
    return lsGet(type === 'week' ? 'softly-meal-plan-week' : 'softly-meal-plan-month', {})
  }
}

export async function saveMealPlan(type, data) {
  lsSet(type === 'week' ? 'softly-meal-plan-week' : 'softly-meal-plan-month', data)
  try { return await apiFetch(`/cooking/meal-plan/${type}`, { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveMealPlan fallback', e); return { ok: true, local: true } }
}

export async function getShoppingList() {
  try { return await apiFetch('/cooking/shopping') }
  catch (e) { console.warn('[api] getShoppingList fallback', e); return lsGet('softly-shopping-list', []) }
}

export async function saveShoppingList(items) {
  lsSet('softly-shopping-list', items)
  try { return await apiFetch('/cooking/shopping', { method: 'PUT', body: JSON.stringify({ data: items }) }) }
  catch (e) { console.warn('[api] saveShoppingList fallback', e); return { ok: true, local: true } }
}

export async function getCookingNotes() {
  try { return await apiFetch('/cooking/notes') }
  catch (e) { console.warn('[api] getCookingNotes fallback', e); return lsGet('softly-cooking-notes', []) }
}

export async function saveCookingNote(note) {
  const withId = { ...note, id: note.id || Date.now() }
  lsSet('softly-cooking-notes', [withId, ...lsGet('softly-cooking-notes', [])])
  try { return await apiFetch('/cooking/notes', { method: 'POST', body: JSON.stringify(note) }) }
  catch (e) { console.warn('[api] saveCookingNote fallback', e); return { ok: true, id: withId.id, local: true } }
}

export async function updateCookingNote(id, updates) {
  lsSet('softly-cooking-notes', lsGet('softly-cooking-notes', []).map(n => n.id === id ? { ...n, ...updates } : n))
  try { return await apiFetch(`/cooking/notes/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateCookingNote fallback', e); return { ok: true, local: true } }
}

export async function deleteCookingNote(id) {
  lsSet('softly-cooking-notes', lsGet('softly-cooking-notes', []).filter(n => n.id !== id))
  try { return await apiFetch(`/cooking/notes/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteCookingNote fallback', e); return { ok: true, local: true } }
}

// ─── WRITING ──────────────────────────────────────────────────────────────────

export async function getWritingProjects() {
  try { return await apiFetch('/writing/projects') }
  catch (e) { console.warn('[api] getWritingProjects fallback', e); return lsGet('softly-writing-projects', []) }
}

export async function saveWritingProjects(projects) {
  lsSet('softly-writing-projects', projects)
  try { return await apiFetch('/writing/projects', { method: 'PUT', body: JSON.stringify({ projects }) }) }
  catch (e) { console.warn('[api] saveWritingProjects fallback', e); return { ok: true, local: true } }
}

export async function getActiveProject() {
  try {
    const s = await apiFetch('/settings/writing-active')
    return s.value || lsGet('softly-writing-active', 'w')
  } catch (e) { console.warn('[api] getActiveProject fallback', e); return lsGet('softly-writing-active', 'w') }
}

export async function setActiveProject(pid) {
  lsSet('softly-writing-active', pid)
  try { return await apiFetch('/settings/writing-active', { method: 'PUT', body: JSON.stringify({ value: pid }) }) }
  catch (e) { console.warn('[api] setActiveProject fallback', e); return { ok: true, local: true } }
}

export async function getWritingProjectData(pid) {
  try { return await apiFetch(`/writing/project/${pid}`) }
  catch (e) { console.warn('[api] getWritingProjectData fallback', e); return lsGet(`${pid}-project`, {}) }
}

export async function saveWritingProjectData(pid, data) {
  lsSet(`${pid}-project`, data)
  try { return await apiFetch(`/writing/project/${pid}`, { method: 'PUT', body: JSON.stringify({ data }) }) }
  catch (e) { console.warn('[api] saveWritingProjectData fallback', e); return { ok: true, local: true } }
}

export async function getChapters(pid) {
  try { return await apiFetch(`/writing/chapters/${pid}`) }
  catch (e) { console.warn('[api] getChapters fallback', e); return lsGet(`${pid}-chapters`, []) }
}

export async function saveChapters(pid, chapters) {
  lsSet(`${pid}-chapters`, chapters)
  try { return await apiFetch(`/writing/chapters/${pid}`, { method: 'PUT', body: JSON.stringify({ chapters }) }) }
  catch (e) { console.warn('[api] saveChapters fallback', e); return { ok: true, local: true } }
}

export async function getCharacters(pid) {
  try { return await apiFetch(`/writing/characters/${pid}`) }
  catch (e) { console.warn('[api] getCharacters fallback', e); return lsGet(`${pid}-chars`, []) }
}

export async function saveCharacters(pid, characters) {
  lsSet(`${pid}-chars`, characters)
  try { return await apiFetch(`/writing/characters/${pid}`, { method: 'PUT', body: JSON.stringify({ data: characters }) }) }
  catch (e) { console.warn('[api] saveCharacters fallback', e); return { ok: true, local: true } }
}

export async function getRelationships(pid) {
  try { return await apiFetch(`/writing/relationships/${pid}`) }
  catch (e) { console.warn('[api] getRelationships fallback', e); return lsGet(`${pid}-rels`, []) }
}

export async function saveRelationships(pid, rels) {
  lsSet(`${pid}-rels`, rels)
  try { return await apiFetch(`/writing/relationships/${pid}`, { method: 'PUT', body: JSON.stringify({ data: rels }) }) }
  catch (e) { console.warn('[api] saveRelationships fallback', e); return { ok: true, local: true } }
}

export async function getWritingSessions(pid) {
  try { return await apiFetch(`/writing/sessions/${pid}`) }
  catch (e) { console.warn('[api] getWritingSessions fallback', e); return lsGet(`${pid}-sessions`, []) }
}

export async function saveWritingSession(pid, date, words) {
  const sessions = lsGet(`${pid}-sessions`, [])
  const next = [{ date, words }, ...sessions.filter(s => s.date !== date)].slice(0, 30)
  lsSet(`${pid}-sessions`, next)
  try { return await apiFetch(`/writing/sessions/${pid}`, { method: 'POST', body: JSON.stringify({ date, words }) }) }
  catch (e) { console.warn('[api] saveWritingSession fallback', e); return { ok: true, local: true } }
}

export async function getWritingText(pid, key) {
  try { return await apiFetch(`/writing/text/${pid}/${key}`) }
  catch (e) {
    console.warn('[api] getWritingText fallback', e)
    const lsKey = key === 'general' ? `${pid}-text-general` : `${pid}-${key}`
    return lsGet(lsKey, '')
  }
}

export async function saveWritingText(pid, key, content) {
  const lsKey = key === 'general' ? `${pid}-text-general` : `${pid}-${key}`
  lsSet(lsKey, content)
  try { return await apiFetch(`/writing/text/${pid}/${key}`, { method: 'PUT', body: JSON.stringify({ content }) }) }
  catch (e) { console.warn('[api] saveWritingText fallback', e); return { ok: true, local: true } }
}

// ─── READING SESSIONS ─────────────────────────────────────────────────────────

export async function getReadingSessions(bookId) {
  try { return await apiFetch(`/reading-sessions?book_id=${bookId}`) }
  catch (e) {
    console.warn('[api] getReadingSessions fallback', e)
    return lsGet('softly-reading-sessions', []).filter(s => s.book_id === bookId)
  }
}

export async function saveReadingSession(session) {
  const all  = lsGet('softly-reading-sessions', [])
  lsSet('softly-reading-sessions', [session, ...all])
  try { return await apiFetch('/reading-sessions', { method: 'POST', body: JSON.stringify(session) }) }
  catch (e) { console.warn('[api] saveReadingSession fallback', e); return { ok: true, local: true } }
}

// ─── READING CHALLENGES ───────────────────────────────────────────────────────

export async function getReadingChallenges(year) {
  const y = year || new Date().getFullYear()
  try { return await apiFetch(`/reading-challenges?year=${y}`) }
  catch (e) {
    console.warn('[api] getReadingChallenges fallback', e)
    return lsGet('softly-reading-challenges', []).filter(c => c.year === y)
  }
}

export async function saveReadingChallenge(challenge) {
  const all = lsGet('softly-reading-challenges', [])
  lsSet('softly-reading-challenges', [challenge, ...all])
  try { return await apiFetch('/reading-challenges', { method: 'POST', body: JSON.stringify(challenge) }) }
  catch (e) { console.warn('[api] saveReadingChallenge fallback', e); return { ok: true, local: true } }
}

export async function updateReadingChallenge(id, updates) {
  const all = lsGet('softly-reading-challenges', [])
  lsSet('softly-reading-challenges', all.map(c => c.id === id ? { ...c, ...updates } : c))
  try { return await apiFetch(`/reading-challenges/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateReadingChallenge fallback', e); return { ok: true, local: true } }
}

export async function getReadingVault() {
  try { return await apiFetch('/reading-challenges/vault') }
  catch (e) { console.warn('[api] getReadingVault fallback', e); return lsGet('softly-reading-vault', []) }
}

export async function saveReadingVaultItem(item) {
  const all = lsGet('softly-reading-vault', [])
  if (!all.find(v => v.item_key === item.item_key)) lsSet('softly-reading-vault', [item, ...all])
  try { return await apiFetch('/reading-challenges/vault', { method: 'POST', body: JSON.stringify(item) }) }
  catch (e) { console.warn('[api] saveReadingVaultItem fallback', e); return { ok: true, local: true } }
}

// ─── JOURNALING ───────────────────────────────────────────────────────────────

export async function getJournalEntries() {
  try { return await apiFetch('/journal') }
  catch (e) { console.warn('[api] getJournalEntries fallback', e); return lsGet('softly-journal-entries', []) }
}

export async function saveJournalEntries(entries) {
  lsSet('softly-journal-entries', entries)
  try { return await apiFetch('/journal', { method: 'PUT', body: JSON.stringify({ entries }) }) }
  catch (e) { console.warn('[api] saveJournalEntries fallback', e); return { ok: true, local: true } }
}

// ─── BRAINSTORM ───────────────────────────────────────────────────────────────

export async function getBrainstormIdeas(projectId) {
  const q = projectId ? `?project_id=${projectId}` : ''
  try { return await apiFetch(`/brainstorm${q}`) }
  catch (e) {
    console.warn('[api] getBrainstormIdeas fallback', e)
    const all = lsGet('softly-brainstorm', [])
    return projectId ? all.filter(i => i.project_id === projectId) : all.filter(i => !i.project_id)
  }
}

export async function saveBrainstormIdea(idea) {
  const all = lsGet('softly-brainstorm', [])
  lsSet('softly-brainstorm', [idea, ...all])
  try { return await apiFetch('/brainstorm', { method: 'POST', body: JSON.stringify(idea) }) }
  catch (e) { console.warn('[api] saveBrainstormIdea fallback', e); return { ok: true, id: idea.id, local: true } }
}

export async function updateBrainstormIdea(id, updates) {
  const all = lsGet('softly-brainstorm', [])
  lsSet('softly-brainstorm', all.map(i => i.id === id ? { ...i, ...updates } : i))
  try { return await apiFetch(`/brainstorm/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateBrainstormIdea fallback', e); return { ok: true, local: true } }
}

export async function deleteBrainstormIdea(id) {
  lsSet('softly-brainstorm', lsGet('softly-brainstorm', []).filter(i => i.id !== id))
  try { return await apiFetch(`/brainstorm/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteBrainstormIdea fallback', e); return { ok: true, local: true } }
}

// ─── WRITING COMMENTS ─────────────────────────────────────────────────────────

export async function getWritingComments({ projectId, chapterId } = {}) {
  const params = new URLSearchParams()
  if (chapterId) params.set('chapter_id', chapterId)
  else if (projectId) params.set('project_id', projectId)
  const q = params.toString() ? `?${params}` : ''
  try { return await apiFetch(`/writing-comments${q}`) }
  catch (e) {
    console.warn('[api] getWritingComments fallback', e)
    const all = lsGet('softly-writing-comments', [])
    if (chapterId) return all.filter(c => c.chapter_id === chapterId)
    if (projectId) return all.filter(c => c.project_id === projectId)
    return all
  }
}

export async function saveWritingComment(comment) {
  const all = lsGet('softly-writing-comments', [])
  const withId = { ...comment, id: comment.id || Date.now() }
  lsSet('softly-writing-comments', [withId, ...all])
  try { return await apiFetch('/writing-comments', { method: 'POST', body: JSON.stringify(comment) }) }
  catch (e) { console.warn('[api] saveWritingComment fallback', e); return { ok: true, id: withId.id, local: true } }
}

export async function updateWritingComment(id, updates) {
  const all = lsGet('softly-writing-comments', [])
  lsSet('softly-writing-comments', all.map(c => c.id === id ? { ...c, ...updates } : c))
  try { return await apiFetch(`/writing-comments/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateWritingComment fallback', e); return { ok: true, local: true } }
}

export async function deleteWritingComment(id) {
  lsSet('softly-writing-comments', lsGet('softly-writing-comments', []).filter(c => c.id !== id))
  try { return await apiFetch(`/writing-comments/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteWritingComment fallback', e); return { ok: true, local: true } }
}

// ─── PLOT BEATS ───────────────────────────────────────────────────────────────

export async function getPlotBeats(projectId) {
  const q = projectId ? `?project_id=${projectId}` : ''
  try { return await apiFetch(`/plot-beats${q}`) }
  catch (e) {
    console.warn('[api] getPlotBeats fallback', e)
    return lsGet(`${projectId}-plot-beats`, [])
  }
}

export async function savePlotBeat(beat) {
  const key = `${beat.project_id || ''}-plot-beats`
  const all = lsGet(key, [])
  lsSet(key, [beat, ...all])
  try { return await apiFetch('/plot-beats', { method: 'POST', body: JSON.stringify(beat) }) }
  catch (e) { console.warn('[api] savePlotBeat fallback', e); return { ok: true, id: beat.id, local: true } }
}

export async function updatePlotBeat(id, updates, projectId) {
  const key = `${projectId || ''}-plot-beats`
  const all = lsGet(key, [])
  lsSet(key, all.map(b => b.id === id ? { ...b, ...updates } : b))
  try { return await apiFetch(`/plot-beats/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updatePlotBeat fallback', e); return { ok: true, local: true } }
}

export async function deletePlotBeat(id, projectId) {
  const key = `${projectId || ''}-plot-beats`
  lsSet(key, lsGet(key, []).filter(b => b.id !== id))
  try { return await apiFetch(`/plot-beats/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deletePlotBeat fallback', e); return { ok: true, local: true } }
}

// ─── MY PAGES ─────────────────────────────────────────────────────────────────

export async function getMyPages() {
  try { return await apiFetch('/my-pages') }
  catch (e) { console.warn('[api] getMyPages fallback', e); return lsGet('ms-pages', []) }
}

export async function createMyPage(page) {
  const next = [{ ...page, id: Date.now() }, ...lsGet('ms-pages', [])]
  lsSet('ms-pages', next)
  try { return await apiFetch('/my-pages', { method: 'POST', body: JSON.stringify(page) }) }
  catch (e) { console.warn('[api] createMyPage fallback', e); return { ok: true, id: Date.now(), local: true } }
}

export async function updateMyPage(id, updates) {
  const pages = lsGet('ms-pages', [])
  lsSet('ms-pages', pages.map(p => p.id === id ? { ...p, ...updates } : p))
  try { return await apiFetch(`/my-pages/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateMyPage fallback', e); return { ok: true, local: true } }
}

export async function deleteMyPage(id) {
  lsSet('ms-pages', lsGet('ms-pages', []).filter(p => p.id !== id))
  try { return await apiFetch(`/my-pages/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteMyPage fallback', e); return { ok: true, local: true } }
}

// ─── PAGE BLOCKS ──────────────────────────────────────────────────────────────

export async function getPageBlocks(pageId) {
  try { return await apiFetch(`/page-blocks?page_id=${pageId}`) }
  catch (e) { console.warn('[api] getPageBlocks fallback', e); return lsGet(`ms-blocks-${pageId}`, []) }
}

export async function createPageBlock(block) {
  const key = `ms-blocks-${block.page_id}`
  const next = [...lsGet(key, []), { ...block, id: Date.now() }]
  lsSet(key, next)
  try { return await apiFetch('/page-blocks', { method: 'POST', body: JSON.stringify(block) }) }
  catch (e) { console.warn('[api] createPageBlock fallback', e); return { ok: true, id: Date.now(), local: true } }
}

export async function updatePageBlock(id, pageId, updates) {
  const key = `ms-blocks-${pageId}`
  lsSet(key, lsGet(key, []).map(b => b.id === id ? { ...b, ...updates } : b))
  try { return await apiFetch(`/page-blocks/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updatePageBlock fallback', e); return { ok: true, local: true } }
}

export async function deletePageBlock(id, pageId) {
  const key = `ms-blocks-${pageId}`
  lsSet(key, lsGet(key, []).filter(b => b.id !== id))
  try { return await apiFetch(`/page-blocks/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deletePageBlock fallback', e); return { ok: true, local: true } }
}

export async function reorderPageBlocks(blocks) {
  try { return await apiFetch('/page-blocks/reorder', { method: 'POST', body: JSON.stringify({ blocks }) }) }
  catch (e) { console.warn('[api] reorderPageBlocks fallback', e); return { ok: true, local: true } }
}

// ─── MY TRACKERS ──────────────────────────────────────────────────────────────

export async function getMyTrackers() {
  try { return await apiFetch('/my-trackers') }
  catch (e) { console.warn('[api] getMyTrackers fallback', e); return lsGet('ms-trackers', []) }
}

export async function createMyTracker(tracker) {
  const next = [{ ...tracker, id: Date.now() }, ...lsGet('ms-trackers', [])]
  lsSet('ms-trackers', next)
  try { return await apiFetch('/my-trackers', { method: 'POST', body: JSON.stringify(tracker) }) }
  catch (e) { console.warn('[api] createMyTracker fallback', e); return { ok: true, id: Date.now(), local: true } }
}

export async function updateMyTracker(id, updates) {
  lsSet('ms-trackers', lsGet('ms-trackers', []).map(t => t.id === id ? { ...t, ...updates } : t))
  try { return await apiFetch(`/my-trackers/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateMyTracker fallback', e); return { ok: true, local: true } }
}

export async function deleteMyTracker(id) {
  lsSet('ms-trackers', lsGet('ms-trackers', []).filter(t => t.id !== id))
  try { return await apiFetch(`/my-trackers/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteMyTracker fallback', e); return { ok: true, local: true } }
}

// ─── TRACKER ENTRIES ──────────────────────────────────────────────────────────

export async function getTrackerEntries(trackerId, from, to) {
  let path = `/tracker-entries?tracker_id=${trackerId}`
  if (from) path += `&from=${from}`
  if (to)   path += `&to=${to}`
  try { return await apiFetch(path) }
  catch (e) { console.warn('[api] getTrackerEntries fallback', e); return lsGet(`ms-entries-${trackerId}`, []) }
}

export async function logTrackerEntry(entry) {
  const key = `ms-entries-${entry.tracker_id}`
  const existing = lsGet(key, [])
  const filtered = existing.filter(e => e.date !== entry.date)
  lsSet(key, [{ ...entry, id: Date.now() }, ...filtered])
  try { return await apiFetch('/tracker-entries', { method: 'POST', body: JSON.stringify(entry) }) }
  catch (e) { console.warn('[api] logTrackerEntry fallback', e); return { ok: true, id: Date.now(), local: true } }
}

export async function deleteTrackerEntry(id, trackerId) {
  const key = `ms-entries-${trackerId}`
  lsSet(key, lsGet(key, []).filter(e => e.id !== id))
  try { return await apiFetch(`/tracker-entries/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteTrackerEntry fallback', e); return { ok: true, local: true } }
}

// ─── MY SPACE TEMPLATES ───────────────────────────────────────────────────────

export async function getMySpaceTemplates() {
  try { return await apiFetch('/my-space-templates') }
  catch (e) { console.warn('[api] getMySpaceTemplates fallback', e); return lsGet('ms-templates', []) }
}

export async function saveMySpaceTemplate(tmpl) {
  const next = [{ ...tmpl, id: Date.now() }, ...lsGet('ms-templates', [])]
  lsSet('ms-templates', next)
  try { return await apiFetch('/my-space-templates', { method: 'POST', body: JSON.stringify(tmpl) }) }
  catch (e) { console.warn('[api] saveMySpaceTemplate fallback', e); return { ok: true, id: Date.now(), local: true } }
}

export async function deleteMySpaceTemplate(id) {
  lsSet('ms-templates', lsGet('ms-templates', []).filter(t => t.id !== id))
  try { return await apiFetch(`/my-space-templates/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteMySpaceTemplate fallback', e); return { ok: true, local: true } }
}

// ─── BOOK REVIEWS ─────────────────────────────────────────────────────────────

export async function getBookReviews(bookId) {
  const q = bookId != null ? `?book_id=${bookId}` : ''
  try { return await apiFetch(`/book-reviews${q}`) }
  catch (e) {
    console.warn('[api] getBookReviews fallback', e)
    const all = lsGet('softly-book-reviews', [])
    return bookId != null ? all.filter(r => r.book_id === bookId) : all
  }
}

export async function saveBookReview(review) {
  const withId = { ...review, id: review.id || Date.now(), is_archived: 0, created_at: new Date().toISOString() }
  lsSet('softly-book-reviews', [withId, ...lsGet('softly-book-reviews', [])])
  try { return await apiFetch('/book-reviews', { method: 'POST', body: JSON.stringify(review) }) }
  catch (e) { console.warn('[api] saveBookReview fallback', e); return { ok: true, id: withId.id, local: true } }
}

export async function updateBookReview(id, updates) {
  lsSet('softly-book-reviews', lsGet('softly-book-reviews', []).map(r => r.id === id ? { ...r, ...updates } : r))
  try { return await apiFetch(`/book-reviews/${id}`, { method: 'PUT', body: JSON.stringify(updates) }) }
  catch (e) { console.warn('[api] updateBookReview fallback', e); return { ok: true, local: true } }
}

export async function deleteBookReview(id) {
  lsSet('softly-book-reviews', lsGet('softly-book-reviews', []).filter(r => r.id !== id))
  try { return await apiFetch(`/book-reviews/${id}`, { method: 'DELETE' }) }
  catch (e) { console.warn('[api] deleteBookReview fallback', e); return { ok: true, local: true } }
}

// ─── APP SETTINGS ─────────────────────────────────────────────────────────────

export async function getSetting(key, fallback = '') {
  try {
    const s = await apiFetch(`/settings/${key}`)
    return s.value ?? fallback
  } catch (e) { console.warn('[api] getSetting fallback', e); return fallback }
}

export async function saveSetting(key, value) {
  try { return await apiFetch(`/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }) }
  catch (e) { console.warn('[api] saveSetting fallback', e); return { ok: true, local: true } }
}
